// Which bytes should the browser actually load for an asset, and how much
// rotation does it still owe on top of them?
//
// The corpus is whatever a decade of iPhones (and a couple of cameras)
// produced: HEIC, JPEG, PNG, GIF, WebP, AVIF, Sony ARW, HEIC sequences,
// QuickTime/HEVC, MP4/H.264, 3GP. A browser decodes maybe half of that
// list. For everything else the server keeps a twin — full-resolution JPEG
// for stills, faststart H.264 MP4 for video, both under
// `alternate_transcoded` — and this module is the single place that decides
// when to reach for it.
//
// A Live Photo needs two twins from one asset (the still AND the motion
// clip), so the motion twin carries its own marker,
// `alternate_live_motion`; the single `alternate_transcoded` slot per asset
// is already spoken for. See `livePhotoPlaybackSource`.
//
// Why prefer the transcode over the original for HEIC even in Safari,
// which decodes HEIC natively: one predictable code path. The alternate is
// full resolution (not a 1024px thumbnail, which is what the viewer used
// to fall back to) and JPEG q0.92 off a HEIC source is visually
// indistinguishable, so the only cost is a somewhat larger download in
// Safari — worth it to have every browser render byte-identical pixels.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import type { AssetDTO, AssetDetailDTO, AssetResourceDTO } from '../api/types';

/// Server-side derivative markers (see `TranscodeJob.resourceTypeValue`
/// and `EditRenderJob`). Everything else in `type` is a PHAssetResourceType
/// raw value.
const TRANSCODED = 'alternate_transcoded';

/// The browser-playable twin of a Live Photo's motion clip. Separate from
/// `alternate_transcoded` because that slot on a Live Photo already holds
/// the *still's* JPEG twin — see `TranscodeJob.liveMotionResourceTypeValue`.
const LIVE_MOTION_TRANSCODED = 'alternate_live_motion';

/// PHAssetResourceType raw values for the Live Photo motion clip:
/// 9 = .pairedVideo, 10 = .fullSizePairedVideo.
const LIVE_MOTION_TYPES = new Set(['9', '10']);

/// UTIs a current browser decodes in an `<img>` without help. Anything
/// outside this set needs the transcoded twin (HEIC, HEIF, HEIC sequences,
/// every camera RAW, TIFF…).
const BROWSER_IMAGE_UTIS = new Set([
  'public.jpeg',
  'public.png',
  'com.compuserve.gif',
  'org.webmproject.webp',
  'public.avif',
  'public.svg-image',
]);

/// UTIs a browser plays in a `<video>`. `.mov` is deliberately absent:
/// Chrome and Safari usually manage H.264-in-QuickTime, Firefox does not,
/// and the container tells us nothing about the codec inside — so we
/// always prefer the MP4 alternate when the server has made one.
const BROWSER_VIDEO_UTIS = new Set(['public.mpeg-4']);

export function isBrowserImageUTI(uti: string): boolean {
  return BROWSER_IMAGE_UTIS.has(uti.toLowerCase());
}

export function isBrowserVideoUTI(uti: string): boolean {
  return BROWSER_VIDEO_UTIS.has(uti.toLowerCase());
}

type AnyAsset = AssetDTO | AssetDetailDTO;

function resourceByID(
  asset: AnyAsset,
  id: string | null | undefined,
): AssetResourceDTO | null {
  if (!id) return null;
  return asset.resources.find((r) => r.id === id) ?? null;
}

function transcodedTwin(asset: AnyAsset): AssetResourceDTO | null {
  return asset.resources.find((r) => r.type === TRANSCODED) ?? null;
}

/// The Live Photo motion clip as *uploaded*, when this asset has one.
/// ~4.7k assets in the corpus carry a paired video: a couple of seconds,
/// a few MB, and — despite what this comment claimed until 2026-08-30 —
/// **HEVC in a QuickTime container**, not H.264 (58 of 60 sampled were
/// `hvc1` under `ftyp qt  `). Safari decodes that; Chrome and Firefox
/// decode neither the codec nor the container.
///
/// This is the *identity* accessor — "does this asset have a paired
/// video?" — and it deliberately matches the server's `?live=` predicate
/// so a filtered page and the badge never disagree. For bytes to hand a
/// `<video>`, use `livePhotoPlaybackSource`.
export function livePhotoMotion(asset: AnyAsset): AssetResourceDTO | null {
  if (asset.kind === 'video') return null;
  return asset.resources.find((r) => LIVE_MOTION_TYPES.has(r.type)) ?? null;
}

/// The motion bytes to actually play: the server's MP4 twin when it has
/// one, otherwise the original clip.
///
/// The fallback is a real fallback, not a formality — an asset uploaded
/// before the twin existed, or whose transcode hasn't drained yet, still
/// plays in Safari and still shows the LIVE badge everywhere. The viewer's
/// `onError` catches the browsers where it won't.
export function livePhotoPlaybackSource(asset: AnyAsset): AssetResourceDTO | null {
  const original = livePhotoMotion(asset);
  if (!original) return null;
  const twin = asset.resources.find(
    (r) => r.type === LIVE_MOTION_TRANSCODED && isBrowserVideoUTI(r.uti),
  );
  return twin ?? original;
}

export function isLivePhoto(asset: AnyAsset): boolean {
  return livePhotoMotion(asset) !== null;
}

export interface DisplaySource {
  resource: AssetResourceDTO;
  /// Bearer-authed path for the bytes.
  path: string;
  /// Clockwise degrees the client must still apply. `display_rotation`
  /// minus whatever the server already baked into these particular bytes
  /// (non-zero only for rotated H.264 alternates).
  rotation: number;
  /// True when we picked a server transcode over the uploaded original.
  transcoded: boolean;
}

export function resourcePath(assetID: string, resourceID: string): string {
  return `/v1/assets/${assetID}/resources/${resourceID}`;
}

/// Rotation still owed after accounting for what the bytes already carry.
export function residualRotation(
  asset: AnyAsset,
  resource: AssetResourceDTO | null,
): number {
  const wanted = asset.display_rotation ?? 0;
  const baked = resource?.baked_rotation ?? 0;
  return (((wanted - baked) % 360) + 360) % 360;
}

/// Pick the best full-size rendition for `asset`, or null when it has no
/// usable resource at all (caller falls back to the large thumbnail).
export function pickDisplaySource(asset: AnyAsset): DisplaySource | null {
  const primary = resourceByID(asset, asset.primary_resource_id);
  const twin = transcodedTwin(asset);

  const choose = (r: AssetResourceDTO, transcoded: boolean): DisplaySource => ({
    resource: r,
    path: resourcePath(asset.id, r.id),
    rotation: residualRotation(asset, r),
    transcoded,
  });

  if (asset.kind === 'video') {
    // Prefer the H.264 MP4 twin: it is the only rendition guaranteed to
    // play everywhere, it is faststart (progressive playback), and it is
    // where a rotation gets baked so the native controls stay upright.
    if (twin && isBrowserVideoUTI(twin.uti)) return choose(twin, true);
    if (primary) return choose(primary, false);
    return null;
  }

  // Stills. A Live Photo's transcoded twin may be an MP4 (the motion clip
  // got transcoded rather than the still) — check the UTI, never the type.
  if (primary) {
    if (isBrowserImageUTI(primary.uti)) return choose(primary, false);
    if (twin && isBrowserImageUTI(twin.uti)) return choose(twin, true);
    // No twin: hand the original over anyway. Safari decodes HEIC, and
    // the viewer's onError path still has the thumbnail to fall back on.
    return choose(primary, false);
  }
  if (twin && isBrowserImageUTI(twin.uti)) return choose(twin, true);
  return null;
}

/// Measure an element and keep the size current. Pairs with
/// `rotationStyle`, which needs the container's real pixel size to swap
/// the max dimensions on a quarter turn.
export function useElementBox<T extends HTMLElement>(): [
  RefObject<T | null>,
  { width: number; height: number },
] {
  const ref = useRef<T>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, box];
}

/// CSS for a rotated element inside a `box`-sized container. A quarter
/// turn swaps which container edge constrains which image edge, and the
/// layout box does NOT rotate with the transform — so the max dimensions
/// have to be swapped by hand or a landscape photo turned 90° spills past
/// the top and bottom of the screen.
export function rotationStyle(
  rotation: number,
  box: { width: number; height: number },
): CSSProperties {
  if (!rotation) return {};
  const style: CSSProperties = { transform: `rotate(${rotation}deg)` };
  if ((rotation === 90 || rotation === 270) && box.width > 0 && box.height > 0) {
    style.maxWidth = box.height;
    style.maxHeight = box.width;
  }
  return style;
}
